/**
 * Elite Batch 22 Part 16 — Workflow Templates 189–200
 * CleverTap mobile-first marketing workflows: push notifications as primary channel, email as secondary.
 * All workflows use CleverTap for engagement, Shopify for commerce, Stripe for billing,
 * OpenAI for AI analysis, Slack for internal alerts, and Google Sheets for logging.
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function clevertapNode(
  id: string,
  label: string,
  y: number,
  method: string,
  endpoint: string,
  bodyKeys: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method,
        url: `https://api.clevertap.com/1/${endpoint}`,
        headers: [
          { key: "X-CleverTap-Account-Id", value: "{clevertap_account_id}" },
          { key: "X-CleverTap-Passcode", value: "{clevertap_passcode}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyKeys,
        variables: vars,
      },
    },
    x,
  );
}
function shopifyNode(
  id: string,
  label: string,
  y: number,
  method: string,
  endpoint: string,
  bodyKeys: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method,
        url: `https://{shopify_store}.myshopify.com/admin/api/2024-01/${endpoint}`,
        headers: [
          { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyKeys,
        variables: vars,
      },
    },
    x,
  );
}
function stripeNode(
  id: string,
  label: string,
  y: number,
  method: string,
  endpoint: string,
  bodyKeys: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method,
        url: `https://api.stripe.com/v1/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {stripe_secret_key}" },
          { key: "Content-Type", value: "application/x-www-form-urlencoded" },
        ],
        body: bodyKeys,
        variables: vars,
      },
    },
    x,
  );
}

export const ELITE_BATCH22_PART16: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ─── 189. App Onboarding Journey | Pattern A ───
  "App Onboarding Journey": {
    nodes: [
      trigger("CleverTap: App install event received", "webhook_app", {
        source: "clevertap",
        event: "app_installed",
        howItStarts:
          "Fires when a new user installs the app and launches it for the first time, captured by CleverTap SDK.",
        setup:
          "Connect CleverTap webhook to fire on 'App Installed' event. Ensure CleverTap SDK is integrated in your mobile app.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Extract new user profile details",
        280,
        "You are a mobile analytics data extractor. Parse the CleverTap app install webhook payload and extract user details regardless of field naming.",
        "Extract from this CleverTap webhook: {webhook_payload}. Return JSON with: user_id (string), device_type (string, ios or android), app_version (string), user_email (string or empty), user_name (string or empty), install_source (string, e.g. organic, paid, referral).",
        [
          { field: "user_id", as: "user_id", type: "string" },
          { field: "device_type", as: "device_type", type: "string" },
          { field: "app_version", as: "app_version", type: "string" },
          { field: "user_email", as: "user_email", type: "string" },
          { field: "user_name", as: "user_name", type: "string" },
          { field: "install_source", as: "install_source", type: "string" },
        ],
      ),
      clevertapNode(
        "3",
        "CleverTap: Fetch user profile and past activity",
        520,
        "POST",
        "upload",
        [{ key: "d", value: '[{"identity":"{user_id}","type":"profile"}]' }],
        [
          { name: "processed", customName: "ct_profile_found", type: "number" },
          { name: "unprocessed", customName: "ct_unprocessed", type: "number" },
        ],
      ),
      n("4", "condition", "Check: Is this a returning user?", 760, {
        conditionDetails: {
          conditions: [
            {
              field: "{ct_profile_found}",
              operator: "greater_than",
              value: "0",
            },
          ],
        },
      }),
      clevertapNode(
        "5",
        "CleverTap: Send welcome push notification",
        1000,
        "POST",
        "send/push.json",
        [
          { key: "to", value: '{"identity":["{user_id}"]}' },
          { key: "tag_group", value: "onboarding" },
          {
            key: "content",
            value:
              '{"title":"Welcome to the app, {user_name}!","body":"Tap here to set up your profile and discover what you can do. Your personalized journey starts now.","platform_specific":{"android":{"notification_icon":"ic_launcher"}}}',
          },
        ],
        [
          { name: "status", customName: "push_status", type: "string" },
          { name: "id", customName: "push_message_id", type: "string" },
        ],
      ),
      n("6", "delayAction", "Wait 24 hours for user exploration", 1240, {
        delayDetails: { duration: 24, unit: "hours" },
      }),
      n("7", "condition", "Check: Has user completed profile setup?", 1480, {
        conditionDetails: {
          conditions: [
            { field: "{push_status}", operator: "equals", value: "success" },
          ],
        },
      }),
      emailNode(
        "8",
        "Email: Send onboarding guide with key features",
        1720,
        "{user_email}",
        "Your quick-start guide to getting the most out of the app",
        "Hi {user_name},\n\nWe noticed you haven't completed your profile setup yet. Here are 3 things you can do right now:\n\n1. Complete your profile to get personalized recommendations\n2. Browse our top categories curated for {device_type} users\n3. Enable notifications so you never miss a deal\n\nNeed help? Reply to this email or tap the help icon in the app.\n\nHappy exploring!",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_2"),
    ],
    summary:
      "Mobile app onboarding journey triggered by CleverTap app install event. Extracts user details via AI, fetches CleverTap profile, checks if returning user, sends a welcome push notification, waits 24 hours, then checks activation status and sends an email guide for users who haven't completed setup.",
  },

  // ─── 190. Activation Milestone Nudge | Pattern A ───
  "Activation Milestone Nudge": {
    nodes: [
      trigger("CleverTap: User signup completed event", "webhook_app", {
        source: "clevertap",
        event: "user_signup",
        howItStarts:
          "Fires when a user completes signup and account creation inside the mobile app, tracked by CleverTap.",
        setup:
          "Connect CleverTap webhook for 'Signed Up' event. Ensure key feature usage events are also tracked (e.g. first_search, first_favorite, first_purchase).",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Extract signup details and onboarding state",
        280,
        "You are a mobile user activation analyst. Parse the CleverTap signup webhook and extract user details regardless of payload format.",
        "Extract from this CleverTap webhook: {webhook_payload}. Return JSON with: user_id (string), user_email (string), user_name (string), signup_method (string, e.g. email, google, apple), device_os (string), features_used (number, count of distinct features used so far).",
        [
          { field: "user_id", as: "user_id", type: "string" },
          { field: "user_email", as: "user_email", type: "string" },
          { field: "user_name", as: "user_name", type: "string" },
          { field: "signup_method", as: "signup_method", type: "string" },
          { field: "device_os", as: "device_os", type: "string" },
          { field: "features_used", as: "features_used", type: "number" },
        ],
      ),
      clevertapNode(
        "3",
        "CleverTap: Lookup user event history",
        520,
        "POST",
        "events/query.json",
        [
          { key: "event_name", value: "feature_activated" },
          { key: "from", value: "20240101" },
          { key: "to", value: "20251231" },
          { key: "identity", value: "{user_id}" },
        ],
        [
          {
            name: "count",
            customName: "activation_event_count",
            type: "number",
          },
          {
            name: "last_event_time",
            customName: "last_feature_time",
            type: "string",
          },
        ],
      ),
      n(
        "4",
        "condition",
        "Check: Has user activated at least 1 key feature?",
        760,
        {
          conditionDetails: {
            conditions: [
              {
                field: "{activation_event_count}",
                operator: "greater_than",
                value: "0",
              },
            ],
          },
        },
      ),
      clevertapNode(
        "5",
        "CleverTap: Send feature activation push nudge",
        1000,
        "POST",
        "send/push.json",
        [
          { key: "to", value: '{"identity":["{user_id}"]}' },
          { key: "tag_group", value: "activation" },
          {
            key: "content",
            value:
              '{"title":"Unlock your first milestone!","body":"You are just one step away from your first achievement. Try searching for something you love and add it to your favorites.","platform_specific":{"ios":{"category":"activation_nudge"}}}',
          },
        ],
        [
          { name: "status", customName: "nudge_push_status", type: "string" },
          { name: "id", customName: "nudge_push_id", type: "string" },
        ],
      ),
      n("6", "delayAction", "Wait 48 hours for feature discovery", 1240, {
        delayDetails: { duration: 48, unit: "hours" },
      }),
      n(
        "7",
        "condition",
        "Check: Did push notification deliver successfully?",
        1480,
        {
          conditionDetails: {
            conditions: [
              {
                field: "{nudge_push_status}",
                operator: "equals",
                value: "success",
              },
            ],
          },
        },
      ),
      emailNode(
        "8",
        "Email: Feature education with step-by-step tutorial",
        1720,
        "{user_email}",
        "3 features that will change how you use the app",
        "Hi {user_name},\n\nWe want to make sure you're getting the most out of your experience. Here are the top features our power users love:\n\n1. Smart Search — Find exactly what you need in seconds\n2. Favorites — Save items and get notified about price drops\n3. Personalized Feed — The more you use it, the smarter it gets\n\nTap any feature name to jump straight to it in the app.\n\nYour activation milestone is waiting!",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_2"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_2"),
    ],
    summary:
      "Feature activation milestone journey triggered by CleverTap signup events. Extracts user data via AI, queries CleverTap for feature activation history, checks milestone status, sends a push nudge to users who haven't activated key features, waits 48 hours, then follows up with an educational email for those still inactive.",
  },

  // ─── 191. Cart Abandonment Journey | Pattern B ───
  "Cart Abandonment Journey": {
    nodes: [
      trigger("CleverTap: Cart abandoned event received", "webhook_app", {
        source: "clevertap",
        event: "cart_abandoned",
        howItStarts:
          "Fires when CleverTap detects a user has added items to cart but left the app or been inactive for 30+ minutes without purchasing.",
        setup:
          "Connect CleverTap webhook for 'Charged' negative trigger (cart abandoned). Ensure cart events include product IDs and values.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Extract cart and customer details",
        280,
        "You are an e-commerce cart recovery specialist. Parse the CleverTap cart abandonment webhook and extract all cart details regardless of payload structure.",
        "Extract from this CleverTap cart abandoned webhook: {webhook_payload}. Return JSON with: user_id (string), user_email (string), user_name (string), cart_total (number), cart_item_count (number), top_product_name (string, most expensive item), top_product_price (number), currency (string).",
        [
          { field: "user_id", as: "user_id", type: "string" },
          { field: "user_email", as: "user_email", type: "string" },
          { field: "user_name", as: "user_name", type: "string" },
          { field: "cart_total", as: "cart_total", type: "number" },
          { field: "cart_item_count", as: "cart_item_count", type: "number" },
          { field: "top_product_name", as: "top_product_name", type: "string" },
          {
            field: "top_product_price",
            as: "top_product_price",
            type: "number",
          },
          { field: "currency", as: "currency", type: "string" },
        ],
      ),
      shopifyNode(
        "3",
        "Shopify: Verify cart status and product availability",
        520,
        "GET",
        "checkouts.json?status=open&customer_id={user_id}",
        [],
        [
          {
            name: "checkouts[0].total_price",
            customName: "checkout_total",
            type: "string",
          },
          {
            name: "checkouts[0].line_items.length",
            customName: "checkout_items",
            type: "number",
          },
          {
            name: "checkouts[0].abandoned_checkout_url",
            customName: "checkout_url",
            type: "string",
          },
        ],
      ),
      n("4", "condition", "Check: Is cart value above $100?", 760, {
        conditionDetails: {
          conditions: [
            { field: "{cart_total}", operator: "greater_than", value: "100" },
          ],
        },
      }),
      clevertapNode(
        "5",
        "CleverTap: Send cart recovery push notification",
        1000,
        "POST",
        "send/push.json",
        [
          { key: "to", value: '{"identity":["{user_id}"]}' },
          { key: "tag_group", value: "cart_recovery" },
          {
            key: "content",
            value:
              '{"title":"Your cart is waiting!","body":"You left {top_product_name} and {cart_item_count} item(s) worth {currency}{cart_total} in your cart. Complete your purchase before they sell out!","actions":[{"label":"Return to Cart","dl":"app://cart"}]}',
          },
        ],
        [
          {
            name: "status",
            customName: "recovery_push_status",
            type: "string",
          },
          { name: "id", customName: "recovery_push_id", type: "string" },
        ],
      ),
      n("6", "delayAction", "Wait 4 hours before email follow-up", 1240, {
        delayDetails: { duration: 4, unit: "hours" },
      }),
      n(
        "7",
        "condition",
        "Check: Is the cart still open and unpurchased?",
        1480,
        {
          conditionDetails: {
            conditions: [
              {
                field: "{checkout_total}",
                operator: "greater_than",
                value: "0",
              },
            ],
          },
        },
      ),
      emailNode(
        "8",
        "Email: Cart recovery with social proof and urgency",
        1720,
        "{user_email}",
        "Still thinking about {top_product_name}? Here's why customers love it",
        "Hi {user_name},\n\nYour cart with {cart_item_count} item(s) totaling {currency}{cart_total} is still waiting for you.\n\nYour top pick: {top_product_name} ({currency}{top_product_price})\n\nWhat other customers are saying:\n- 'Best purchase I've made this year!'\n- 'Amazing quality for the price'\n\nItems in your cart are popular and selling fast. Complete your purchase now before stock runs out.\n\n[Return to Cart]({checkout_url})\n\nNeed help? Our support team is here for you.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Omnichannel cart abandonment recovery powered by CleverTap and Shopify. Extracts cart details via AI, verifies checkout status in Shopify, evaluates cart value, sends a push notification with product details and deep link, waits 4 hours, then sends an email with social proof and urgency for carts that remain open.",
  },

  // ─── 192. Browse Abandonment Journey | Pattern C ───
  "Browse Abandonment Journey": {
    nodes: [
      trigger("CleverTap: Product viewed without add-to-cart", "webhook_app", {
        source: "clevertap",
        event: "product_viewed",
        howItStarts:
          "Fires when CleverTap detects a user has viewed multiple products but left the app without adding anything to cart within 60 minutes.",
        setup:
          "Connect CleverTap webhook for 'Product Viewed' event with negative filter for 'Added to Cart'. Set minimum 3 product views in the session.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Extract browsed product details",
        280,
        "You are a browse behavior analyst. Parse the CleverTap product view webhook and extract browsing details regardless of payload format.",
        "Extract from this CleverTap browse webhook: {webhook_payload}. Return JSON with: user_id (string), user_email (string), user_name (string), product_names (string, comma-separated list of viewed products), product_category (string, most viewed category), view_count (number), last_viewed_product (string), last_viewed_price (number).",
        [
          { field: "user_id", as: "user_id", type: "string" },
          { field: "user_email", as: "user_email", type: "string" },
          { field: "user_name", as: "user_name", type: "string" },
          { field: "product_names", as: "product_names", type: "string" },
          { field: "product_category", as: "product_category", type: "string" },
          { field: "view_count", as: "view_count", type: "number" },
          {
            field: "last_viewed_product",
            as: "last_viewed_product",
            type: "string",
          },
          {
            field: "last_viewed_price",
            as: "last_viewed_price",
            type: "number",
          },
        ],
      ),
      clevertapNode(
        "3",
        "CleverTap: Fetch user purchase and browse history",
        520,
        "POST",
        "profiles/query.json",
        [
          { key: "identity", value: "{user_id}" },
          {
            key: "properties",
            value:
              '["purchase_count","last_purchase_date","preferred_category"]',
          },
        ],
        [
          {
            name: "record.purchase_count",
            customName: "total_purchases",
            type: "number",
          },
          {
            name: "record.last_purchase_date",
            customName: "last_purchase_date",
            type: "string",
          },
          {
            name: "record.preferred_category",
            customName: "preferred_category",
            type: "string",
          },
        ],
      ),
      n(
        "4",
        "condition",
        "Check: Is this a returning customer with past purchases?",
        760,
        {
          conditionDetails: {
            conditions: [
              {
                field: "{total_purchases}",
                operator: "greater_than",
                value: "0",
              },
            ],
          },
        },
      ),
      clevertapNode(
        "5",
        "CleverTap: Send browse reminder push notification",
        1000,
        "POST",
        "send/push.json",
        [
          { key: "to", value: '{"identity":["{user_id}"]}' },
          { key: "tag_group", value: "browse_recovery" },
          {
            key: "content",
            value:
              '{"title":"Still interested in {product_category}?","body":"You viewed {last_viewed_product} and {view_count} other items. Come back and explore our {product_category} collection — new picks added daily!","actions":[{"label":"Continue Browsing","dl":"app://category/{product_category}"}]}',
          },
        ],
        [
          { name: "status", customName: "browse_push_status", type: "string" },
          { name: "id", customName: "browse_push_id", type: "string" },
        ],
      ),
      n("6", "delayAction", "Wait 24 hours for organic return", 1240, {
        delayDetails: { duration: 24, unit: "hours" },
      }),
      n(
        "7",
        "condition",
        "Check: Has user still not added anything to cart?",
        1480,
        {
          conditionDetails: {
            conditions: [
              {
                field: "{browse_push_status}",
                operator: "equals",
                value: "success",
              },
            ],
          },
        },
      ),
      emailNode(
        "8",
        "Email: Personalized product recommendations from browsed category",
        1720,
        "{user_email}",
        "Curated picks in {product_category} just for you",
        "Hi {user_name},\n\nWe noticed you were exploring {product_category} recently. Here are some items we think you'll love:\n\nYou viewed: {product_names}\n\nPopular in {product_category} right now:\n- Trending items other shoppers are loving\n- New arrivals this week\n- Best-rated products in your price range\n\nAs a returning customer, you get early access to our latest {product_category} additions.\n\nOpen the app to see your personalized feed.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_2"),
    ],
    summary:
      "Push + email browse abandonment recovery via CleverTap. Extracts viewed product details via AI, fetches user purchase history from CleverTap profiles, checks returning customer status, sends a browse reminder push with deep link to the viewed category, waits 24 hours, then sends an email with personalized recommendations for users who still haven't converted.",
  },

  // ─── 193. Personalized Recommendation Journey | Pattern C ───
  "Personalized Recommendation Journey": {
    nodes: [
      trigger("CleverTap: Session ended with browse history", "webhook_app", {
        source: "clevertap",
        event: "session_ended",
        howItStarts:
          "Fires when CleverTap detects a user session ended with significant browse activity (5+ product views) but no conversion action.",
        setup:
          "Connect CleverTap webhook for 'App Session Ended' event with filter for sessions containing 5+ product views and zero 'Added to Cart' events.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Analyze browse patterns and generate recommendations",
        280,
        "You are an AI product recommendation engine. Parse the CleverTap session data and generate personalized product recommendations based on browse patterns, category affinity, and price range preferences.",
        "Analyze this CleverTap session data: {webhook_payload}. Return JSON with: user_id (string), user_email (string), user_name (string), primary_category (string, most browsed), secondary_category (string), avg_price_viewed (number), recommendation_reason (string, a short personalized explanation of why these picks match), recommended_products (string, comma-separated list of 3 product suggestions), push_headline (string, compelling 50-char max headline).",
        [
          { field: "user_id", as: "user_id", type: "string" },
          { field: "user_email", as: "user_email", type: "string" },
          { field: "user_name", as: "user_name", type: "string" },
          { field: "primary_category", as: "primary_category", type: "string" },
          {
            field: "secondary_category",
            as: "secondary_category",
            type: "string",
          },
          { field: "avg_price_viewed", as: "avg_price_viewed", type: "number" },
          {
            field: "recommendation_reason",
            as: "recommendation_reason",
            type: "string",
          },
          {
            field: "recommended_products",
            as: "recommended_products",
            type: "string",
          },
          { field: "push_headline", as: "push_headline", type: "string" },
        ],
      ),
      clevertapNode(
        "3",
        "CleverTap: Fetch user affinity profile and engagement score",
        520,
        "POST",
        "profiles/query.json",
        [
          { key: "identity", value: "{user_id}" },
          {
            key: "properties",
            value:
              '["engagement_score","preferred_categories","push_enabled","email_opt_in"]',
          },
        ],
        [
          {
            name: "record.engagement_score",
            customName: "engagement_score",
            type: "number",
          },
          {
            name: "record.preferred_categories",
            customName: "preferred_categories",
            type: "string",
          },
          {
            name: "record.push_enabled",
            customName: "push_enabled",
            type: "boolean",
          },
        ],
      ),
      n(
        "4",
        "condition",
        "Check: Is user a returning browser with push enabled?",
        760,
        {
          conditionDetails: {
            conditions: [
              {
                field: "{engagement_score}",
                operator: "greater_than",
                value: "30",
              },
            ],
          },
        },
      ),
      clevertapNode(
        "5",
        "CleverTap: Send AI-curated recommendation push",
        1000,
        "POST",
        "send/push.json",
        [
          { key: "to", value: '{"identity":["{user_id}"]}' },
          { key: "tag_group", value: "ai_recommendations" },
          {
            key: "content",
            value:
              '{"title":"{push_headline}","body":"{recommendation_reason} Check out: {recommended_products}","actions":[{"label":"See Picks","dl":"app://recommendations"}]}',
          },
        ],
        [
          { name: "status", customName: "reco_push_status", type: "string" },
          { name: "id", customName: "reco_push_id", type: "string" },
        ],
      ),
      n("6", "delayAction", "Wait 24 hours for engagement", 1240, {
        delayDetails: { duration: 24, unit: "hours" },
      }),
      n("7", "condition", "Check: Did the recommendation push deliver?", 1480, {
        conditionDetails: {
          conditions: [
            {
              field: "{reco_push_status}",
              operator: "equals",
              value: "success",
            },
          ],
        },
      }),
      emailNode(
        "8",
        "Email: Extended recommendations with visual product cards",
        1720,
        "{user_email}",
        "Handpicked for you: {primary_category} finds you'll love",
        "Hi {user_name},\n\n{recommendation_reason}\n\nBased on your browsing in {primary_category} and {secondary_category}, here are our top picks:\n\n{recommended_products}\n\nAll items are in your preferred price range (around ${avg_price_viewed}).\n\nWhy these picks? Our AI analyzed your browsing patterns to find products that match your style and budget. The more you browse, the smarter our recommendations get.\n\nOpen the app to see your full personalized feed.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_2"),
    ],
    summary:
      "AI-powered personalized recommendation journey using CleverTap and OpenAI. Analyzes browse session data to generate tailored product recommendations, fetches user affinity profiles from CleverTap, checks engagement score, sends an AI-curated push notification with personalized products, waits 24 hours, then follows up via email with extended visual recommendations for non-converters.",
  },

  // ─── 194. Churn Prediction Save Play | Pattern L ───
  "Churn Prediction Save Play": {
    nodes: [
      trigger(
        "CleverTap: Churn prediction model flags at-risk user",
        "webhook_app",
        {
          source: "clevertap",
          event: "churn_predicted",
          howItStarts:
            "Fires when CleverTap's churn prediction model flags a user as at-risk based on declining engagement patterns, reduced session frequency, and usage drops.",
          setup:
            "Configure CleverTap's predictive churn model and set up a webhook for users moving into 'likely to churn' segment. Recommended threshold: churn probability > 70%.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "OpenAI: Analyze churn signals and recommend save strategy",
        280,
        "You are a customer retention strategist. Analyze the CleverTap churn prediction data and recommend the best save intervention based on user behavior patterns.",
        "Analyze this churn prediction data: {webhook_payload}. Return JSON with: user_id (string), user_email (string), user_name (string), churn_probability (number, 0-100), days_since_last_session (number), engagement_trend (string: declining, stagnant, or cliff), recommended_action (string: value_reminder, exclusive_offer, personal_outreach, or feature_highlight), save_message (string, a personalized 100-char message addressing likely churn reason), churn_reason (string, likely cause).",
        [
          { field: "user_id", as: "user_id", type: "string" },
          { field: "user_email", as: "user_email", type: "string" },
          { field: "user_name", as: "user_name", type: "string" },
          {
            field: "churn_probability",
            as: "churn_probability",
            type: "number",
          },
          {
            field: "days_since_last_session",
            as: "days_since_last_session",
            type: "number",
          },
          { field: "engagement_trend", as: "engagement_trend", type: "string" },
          {
            field: "recommended_action",
            as: "recommended_action",
            type: "string",
          },
          { field: "save_message", as: "save_message", type: "string" },
          { field: "churn_reason", as: "churn_reason", type: "string" },
        ],
      ),
      clevertapNode(
        "3",
        "CleverTap: Fetch user lifetime value and engagement history",
        520,
        "POST",
        "profiles/query.json",
        [
          { key: "identity", value: "{user_id}" },
          {
            key: "properties",
            value:
              '["lifetime_value","total_purchases","days_as_customer","last_purchase_amount","favorite_category"]',
          },
        ],
        [
          {
            name: "record.lifetime_value",
            customName: "lifetime_value",
            type: "number",
          },
          {
            name: "record.total_purchases",
            customName: "total_purchases",
            type: "number",
          },
          {
            name: "record.days_as_customer",
            customName: "days_as_customer",
            type: "number",
          },
          {
            name: "record.favorite_category",
            customName: "favorite_category",
            type: "string",
          },
        ],
      ),
      n(
        "4",
        "condition",
        "Check: Is this a high-value customer (LTV > $200)?",
        760,
        {
          conditionDetails: {
            conditions: [
              {
                field: "{lifetime_value}",
                operator: "greater_than",
                value: "200",
              },
            ],
          },
        },
      ),
      clevertapNode(
        "5",
        "CleverTap: Send personalized save push notification",
        1000,
        "POST",
        "send/push.json",
        [
          { key: "to", value: '{"identity":["{user_id}"]}' },
          { key: "tag_group", value: "churn_save" },
          {
            key: "content",
            value:
              '{"title":"We miss you, {user_name}!","body":"{save_message}","actions":[{"label":"Come Back","dl":"app://home"}]}',
          },
        ],
        [
          { name: "status", customName: "save_push_status", type: "string" },
          { name: "id", customName: "save_push_id", type: "string" },
        ],
      ),
      n("6", "delayAction", "Wait 5 days for re-engagement", 1240, {
        delayDetails: { duration: 5, unit: "days" },
      }),
      n("7", "condition", "Check: Has user returned to the app?", 1480, {
        conditionDetails: {
          conditions: [
            {
              field: "{save_push_status}",
              operator: "equals",
              value: "success",
            },
          ],
        },
      }),
      slackNode(
        "8",
        "Slack: Alert retention team about at-risk high-value user",
        1720,
        "🚨 *Churn Save Escalation*\n*User:* {user_name} ({user_email})\n*LTV:* ${lifetime_value} | *Purchases:* {total_purchases} | *Customer for:* {days_as_customer} days\n*Churn Probability:* {churn_probability}% | *Trend:* {engagement_trend}\n*Likely Reason:* {churn_reason}\n*AI Recommended Action:* {recommended_action}\n*Status:* Push sent but user has not returned after 5 days — manual outreach recommended.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_2"),
    ],
    summary:
      "Churn prediction intervention journey using CleverTap's predictive model and OpenAI analysis. AI analyzes churn signals and recommends a save strategy, CleverTap provides lifetime value context, sends a personalized save push notification, waits 5 days, then escalates to the retention team via Slack if the user hasn't returned.",
  },

  // ─── 195. Inactive User Winback | Pattern G ───
  "Inactive User Winback": {
    nodes: [
      trigger("CleverTap: 30-day inactive user segment triggered", "schedule", {
        schedule: "Daily at 10:00 AM",
        howItStarts:
          "Runs daily to process users who have crossed the 30-day inactivity threshold in CleverTap's inactive user segment.",
        setup:
          "Create a CleverTap segment for users with no app open or event in 30+ days. Schedule this workflow to run daily and pull the latest segment members.",
        inputFields: [],
      }),
      clevertapNode(
        "2",
        "CleverTap: Fetch inactive user profiles from segment",
        280,
        "POST",
        "profiles/query.json",
        [
          { key: "segment", value: "inactive_30_days" },
          {
            key: "properties",
            value:
              '["identity","email","name","last_app_open","total_purchases","lifetime_value","push_token","favorite_category"]',
          },
        ],
        [
          {
            name: "records[0].identity",
            customName: "user_id",
            type: "string",
          },
          {
            name: "records[0].email",
            customName: "user_email",
            type: "string",
          },
          { name: "records[0].name", customName: "user_name", type: "string" },
          {
            name: "records[0].last_app_open",
            customName: "last_active_date",
            type: "string",
          },
          {
            name: "records[0].total_purchases",
            customName: "total_purchases",
            type: "number",
          },
          {
            name: "records[0].lifetime_value",
            customName: "lifetime_value",
            type: "number",
          },
          {
            name: "records[0].favorite_category",
            customName: "favorite_category",
            type: "string",
          },
        ],
      ),
      aiNode(
        "3",
        "OpenAI: Generate personalized winback message",
        520,
        "You are a customer winback specialist. Given an inactive user's profile, craft a compelling and personalized push notification message and email subject line to bring them back.",
        "Generate winback content for: user {user_name}, inactive since {last_active_date}, {total_purchases} past purchases, LTV ${lifetime_value}, favorite category: {favorite_category}. Return JSON with: push_title (string, 40 chars max), push_body (string, 100 chars max, personalized), email_subject (string, 60 chars max), winback_angle (string: new_features, exclusive_offer, missed_items, or community).",
        [
          { field: "push_title", as: "push_title", type: "string" },
          { field: "push_body", as: "push_body", type: "string" },
          { field: "email_subject", as: "email_subject", type: "string" },
          { field: "winback_angle", as: "winback_angle", type: "string" },
        ],
      ),
      n(
        "4",
        "condition",
        "Check: Is user a high-value customer (LTV > $150)?",
        760,
        {
          conditionDetails: {
            conditions: [
              {
                field: "{lifetime_value}",
                operator: "greater_than",
                value: "150",
              },
            ],
          },
        },
      ),
      clevertapNode(
        "5",
        "CleverTap: Send winback push notification",
        1000,
        "POST",
        "send/push.json",
        [
          { key: "to", value: '{"identity":["{user_id}"]}' },
          { key: "tag_group", value: "winback" },
          {
            key: "content",
            value:
              '{"title":"{push_title}","body":"{push_body}","actions":[{"label":"Open App","dl":"app://home"}]}',
          },
        ],
        [
          { name: "status", customName: "winback_push_status", type: "string" },
          { name: "id", customName: "winback_push_id", type: "string" },
        ],
      ),
      n("6", "delayAction", "Wait 7 days for re-engagement", 1240, {
        delayDetails: { duration: 7, unit: "days" },
      }),
      n("7", "condition", "Check: Has user reopened the app?", 1480, {
        conditionDetails: {
          conditions: [
            {
              field: "{winback_push_status}",
              operator: "equals",
              value: "success",
            },
          ],
        },
      }),
      emailNode(
        "8",
        "Email: Final winback with exclusive return offer",
        1720,
        "{user_email}",
        "{email_subject}",
        "Hi {user_name},\n\nIt's been a while since we've seen you, and a lot has changed!\n\nSince your last visit, we've added:\n- Fresh arrivals in {favorite_category}\n- Improved app experience with faster browsing\n- Exclusive deals for returning members\n\nAs a valued customer with {total_purchases} purchases, we'd love to welcome you back with a special offer just for you.\n\nOpen the app to see what's new and claim your return reward.\n\nWe hope to see you soon!",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_2"),
    ],
    summary:
      "Push + email winback journey for 30-day inactive users via CleverTap. Fetches inactive user profiles from a CleverTap segment, generates personalized winback content via OpenAI, checks customer lifetime value, sends a tailored push notification, waits 7 days, then sends a final winback email with an exclusive return offer for users who haven't re-engaged.",
  },

  // ─── 196. Purchase Anniversary Journey | Pattern H ───
  "Purchase Anniversary Journey": {
    nodes: [
      trigger("CleverTap: Purchase anniversary date reached", "schedule", {
        schedule: "Daily at 9:00 AM",
        howItStarts:
          "Runs daily to identify users whose first purchase anniversary is today, celebrating their customer milestone.",
        setup:
          "Create a CleverTap segment for users whose first_purchase_date matches today's date (anniversary). Schedule this workflow daily.",
        inputFields: [],
      }),
      clevertapNode(
        "2",
        "CleverTap: Fetch anniversary customer profiles and LTV",
        280,
        "POST",
        "profiles/query.json",
        [
          { key: "segment", value: "purchase_anniversary_today" },
          {
            key: "properties",
            value:
              '["identity","email","name","first_purchase_date","total_purchases","lifetime_value","favorite_category","vip_tier"]',
          },
        ],
        [
          {
            name: "records[0].identity",
            customName: "user_id",
            type: "string",
          },
          {
            name: "records[0].email",
            customName: "user_email",
            type: "string",
          },
          { name: "records[0].name", customName: "user_name", type: "string" },
          {
            name: "records[0].first_purchase_date",
            customName: "first_purchase_date",
            type: "string",
          },
          {
            name: "records[0].total_purchases",
            customName: "total_purchases",
            type: "number",
          },
          {
            name: "records[0].lifetime_value",
            customName: "lifetime_value",
            type: "number",
          },
          {
            name: "records[0].favorite_category",
            customName: "favorite_category",
            type: "string",
          },
          {
            name: "records[0].vip_tier",
            customName: "vip_tier",
            type: "string",
          },
        ],
      ),
      n("3", "condition", "Check: Is customer a VIP tier member?", 520, {
        conditionDetails: {
          conditions: [
            { field: "{vip_tier}", operator: "equals", value: "vip" },
          ],
        },
      }),
      clevertapNode(
        "4",
        "CleverTap: Send anniversary celebration push",
        760,
        "POST",
        "send/push.json",
        [
          { key: "to", value: '{"identity":["{user_id}"]}' },
          { key: "tag_group", value: "anniversary" },
          {
            key: "content",
            value:
              '{"title":"Happy Anniversary, {user_name}!","body":"It has been a year since your first purchase! Thank you for being part of our family. You have made {total_purchases} purchases and we have a special reward to celebrate.","actions":[{"label":"Claim Reward","dl":"app://rewards/anniversary"}]}',
          },
        ],
        [
          {
            name: "status",
            customName: "anniversary_push_status",
            type: "string",
          },
          { name: "id", customName: "anniversary_push_id", type: "string" },
        ],
      ),
      n("5", "delayAction", "Wait 5 days for reward redemption", 1000, {
        delayDetails: { duration: 5, unit: "days" },
      }),
      n("6", "condition", "Check: Has the reward been redeemed?", 1240, {
        conditionDetails: {
          conditions: [
            {
              field: "{anniversary_push_status}",
              operator: "equals",
              value: "success",
            },
          ],
        },
      }),
      emailNode(
        "7",
        "Email: Anniversary reward reminder with appreciation",
        1480,
        "{user_email}",
        "Your anniversary reward is waiting, {user_name}!",
        "Hi {user_name},\n\nHappy customer anniversary! One year ago, you made your first purchase with us, and we couldn't be more grateful.\n\nHere's a look at your journey:\n- Total purchases: {total_purchases}\n- Favorite category: {favorite_category}\n- Customer tier: {vip_tier}\n\nYour anniversary reward is still waiting to be redeemed. Don't let it expire!\n\nAs a thank you for your loyalty, enjoy exclusive access to our anniversary collection in {favorite_category}.\n\nClaim your reward in the app before it expires.",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log anniversary milestone and engagement",
        1720,
        "Anniversaries",
        '[["{user_id}","{user_name}","{user_email}","{first_purchase_date}","{total_purchases}","{lifetime_value}","{vip_tier}","{anniversary_push_status}","logged"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("3", "4", "output_2"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_2"),
      e("6", "8"),
    ],
    summary:
      "Purchase anniversary celebration journey via CleverTap with Google Sheets logging. Fetches anniversary customer profiles from CleverTap, checks VIP tier status, sends a celebration push with reward deep link, waits 5 days, then sends an email reward reminder for non-redeemers while logging all anniversary milestones to Google Sheets.",
  },

  // ─── 197. Subscription Renewal Reminder | Pattern F ───
  "Subscription Renewal Reminder": {
    nodes: [
      trigger("CleverTap: Subscription renewal approaching", "webhook_app", {
        source: "clevertap",
        event: "renewal_approaching",
        howItStarts:
          "Fires when CleverTap detects a subscription renewal date is 7 days away, based on subscription lifecycle tracking.",
        setup:
          "Configure CleverTap to track subscription start dates and renewal cycles. Set webhook to fire 7 days before renewal_date for active subscribers.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Extract subscription and renewal details",
        280,
        "You are a subscription lifecycle analyst. Parse the CleverTap renewal webhook and extract all subscription details regardless of payload format.",
        "Extract from this renewal webhook: {webhook_payload}. Return JSON with: user_id (string), user_email (string), user_name (string), plan_name (string), renewal_date (string), monthly_price (number), currency (string), subscription_months (number, how long subscribed), auto_renew (boolean).",
        [
          { field: "user_id", as: "user_id", type: "string" },
          { field: "user_email", as: "user_email", type: "string" },
          { field: "user_name", as: "user_name", type: "string" },
          { field: "plan_name", as: "plan_name", type: "string" },
          { field: "renewal_date", as: "renewal_date", type: "string" },
          { field: "monthly_price", as: "monthly_price", type: "number" },
          { field: "currency", as: "currency", type: "string" },
          {
            field: "subscription_months",
            as: "subscription_months",
            type: "number",
          },
          { field: "auto_renew", as: "auto_renew", type: "boolean" },
        ],
      ),
      stripeNode(
        "3",
        "Stripe: Check payment method status for renewal",
        520,
        "GET",
        "customers/{user_id}/payment_methods?type=card",
        [],
        [
          {
            name: "data[0].card.last4",
            customName: "card_last4",
            type: "string",
          },
          {
            name: "data[0].card.exp_month",
            customName: "card_exp_month",
            type: "number",
          },
          {
            name: "data[0].card.exp_year",
            customName: "card_exp_year",
            type: "number",
          },
          {
            name: "data.length",
            customName: "payment_methods_count",
            type: "number",
          },
        ],
      ),
      n("4", "condition", "Check: Is payment method expired or missing?", 760, {
        conditionDetails: {
          conditions: [
            {
              field: "{payment_methods_count}",
              operator: "greater_than",
              value: "0",
            },
          ],
        },
      }),
      clevertapNode(
        "5",
        "CleverTap: Send renewal reminder push notification",
        1000,
        "POST",
        "send/push.json",
        [
          { key: "to", value: '{"identity":["{user_id}"]}' },
          { key: "tag_group", value: "renewal" },
          {
            key: "content",
            value:
              '{"title":"Renewal in 7 days","body":"Your {plan_name} subscription ({currency}{monthly_price}/mo) renews on {renewal_date}. Card ending in {card_last4} will be charged. Manage your subscription in the app.","actions":[{"label":"Manage Subscription","dl":"app://subscription"}]}',
          },
        ],
        [
          { name: "status", customName: "renewal_push_status", type: "string" },
          { name: "id", customName: "renewal_push_id", type: "string" },
        ],
      ),
      n("6", "delayAction", "Wait 5 days until 2 days before renewal", 1240, {
        delayDetails: { duration: 5, unit: "days" },
      }),
      n(
        "7",
        "condition",
        "Check: Is subscription still active and not cancelled?",
        1480,
        {
          conditionDetails: {
            conditions: [
              {
                field: "{renewal_push_status}",
                operator: "equals",
                value: "success",
              },
            ],
          },
        },
      ),
      emailNode(
        "8",
        "Email: Final renewal confirmation with value recap",
        1720,
        "{user_email}",
        "Your {plan_name} subscription renews in 2 days",
        "Hi {user_name},\n\nJust a heads up — your {plan_name} subscription ({currency}{monthly_price}/month) renews on {renewal_date}.\n\nYou've been a subscriber for {subscription_months} months. Here's what you've enjoyed:\n- Full access to all premium features\n- Priority customer support\n- Exclusive member-only content and deals\n\nYour card ending in {card_last4} will be charged {currency}{monthly_price}.\n\nIf you'd like to make any changes, manage your subscription in the app before {renewal_date}.\n\nThank you for being a valued subscriber!",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Push + email subscription renewal reminder journey using CleverTap and Stripe. Extracts subscription details via AI, verifies payment method status in Stripe, checks card validity, sends a renewal reminder push 7 days before renewal, waits 5 days, then sends a final email confirmation with value recap 2 days before the charge.",
  },

  // ─── 198. Payment Failure Recovery | Pattern F ───
  "Payment Failure Recovery": {
    nodes: [
      trigger("CleverTap: Payment failure event from Stripe", "webhook_app", {
        source: "clevertap",
        event: "payment_failed",
        howItStarts:
          "Fires when CleverTap receives a payment failure event synced from Stripe, indicating a subscription charge was declined.",
        setup:
          "Connect Stripe payment failure webhooks to CleverTap via server-side integration. Ensure the payload includes customer ID, invoice details, and failure reason.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Extract payment failure details and classify severity",
        280,
        "You are a payment recovery specialist. Parse the CleverTap payment failure webhook and extract payment details, classify the failure severity, and suggest the best recovery approach.",
        "Extract from this payment failure webhook: {webhook_payload}. Return JSON with: user_id (string), user_email (string), user_name (string), plan_name (string), amount_due (number), currency (string), failure_reason (string, e.g. card_declined, insufficient_funds, expired_card), retry_count (number), days_until_suspension (number), recovery_urgency (string: low, medium, or high).",
        [
          { field: "user_id", as: "user_id", type: "string" },
          { field: "user_email", as: "user_email", type: "string" },
          { field: "user_name", as: "user_name", type: "string" },
          { field: "plan_name", as: "plan_name", type: "string" },
          { field: "amount_due", as: "amount_due", type: "number" },
          { field: "currency", as: "currency", type: "string" },
          { field: "failure_reason", as: "failure_reason", type: "string" },
          { field: "retry_count", as: "retry_count", type: "number" },
          {
            field: "days_until_suspension",
            as: "days_until_suspension",
            type: "number",
          },
          { field: "recovery_urgency", as: "recovery_urgency", type: "string" },
        ],
      ),
      stripeNode(
        "3",
        "Stripe: Fetch latest invoice and retry status",
        520,
        "GET",
        "invoices?customer={user_id}&status=open&limit=1",
        [],
        [
          { name: "data[0].id", customName: "invoice_id", type: "string" },
          {
            name: "data[0].amount_due",
            customName: "stripe_amount_due",
            type: "number",
          },
          {
            name: "data[0].attempt_count",
            customName: "attempt_count",
            type: "number",
          },
          {
            name: "data[0].next_payment_attempt",
            customName: "next_retry_timestamp",
            type: "number",
          },
          {
            name: "data[0].hosted_invoice_url",
            customName: "invoice_url",
            type: "string",
          },
        ],
      ),
      n("4", "condition", "Check: Has payment failed more than 2 times?", 760, {
        conditionDetails: {
          conditions: [
            { field: "{attempt_count}", operator: "greater_than", value: "2" },
          ],
        },
      }),
      clevertapNode(
        "5",
        "CleverTap: Send payment update push notification",
        1000,
        "POST",
        "send/push.json",
        [
          { key: "to", value: '{"identity":["{user_id}"]}' },
          { key: "tag_group", value: "payment_recovery" },
          {
            key: "content",
            value:
              '{"title":"Action needed: Update your payment","body":"Your {plan_name} payment of {currency}{amount_due} could not be processed ({failure_reason}). Update your payment method to keep your subscription active.","actions":[{"label":"Update Payment","dl":"app://billing/update"}]}',
          },
        ],
        [
          { name: "status", customName: "payment_push_status", type: "string" },
          { name: "id", customName: "payment_push_id", type: "string" },
        ],
      ),
      n("6", "delayAction", "Wait 3 days before escalation", 1240, {
        delayDetails: { duration: 3, unit: "days" },
      }),
      n(
        "7",
        "condition",
        "Check: Is invoice still unpaid after 3 days?",
        1480,
        {
          conditionDetails: {
            conditions: [
              {
                field: "{payment_push_status}",
                operator: "equals",
                value: "success",
              },
            ],
          },
        },
      ),
      emailNode(
        "8",
        "Email: Urgent payment update with account suspension warning",
        1720,
        "{user_email}",
        "Urgent: Your {plan_name} subscription will be suspended in {days_until_suspension} days",
        "Hi {user_name},\n\nWe've been unable to process your {plan_name} subscription payment of {currency}{amount_due}.\n\nReason: {failure_reason}\nAttempts: {attempt_count}\n\nYour account will be suspended if payment isn't resolved within {days_until_suspension} days. You'll lose access to:\n- All premium features\n- Your saved preferences and history\n- Member-only benefits\n\nUpdate your payment method now to avoid interruption:\n{invoice_url}\n\nIf you've already updated your payment, you can ignore this email — we'll retry automatically.\n\nNeed help? Contact our billing support team.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_2"),
    ],
    summary:
      "Payment failure dunning journey using CleverTap and Stripe. Extracts failure details via AI, fetches invoice status from Stripe, checks retry count severity, sends a push notification requesting payment update, waits 3 days, then sends an urgent email with suspension warning and hosted invoice link for unresolved payments.",
  },

  // ─── 199. Transactional Order Update Journey | Pattern E ───
  "Transactional Order Update Journey": {
    nodes: [
      trigger("CleverTap: Order placed event from Shopify", "webhook_app", {
        source: "clevertap",
        event: "order_placed",
        howItStarts:
          "Fires when CleverTap receives an order placed event synced from Shopify, triggering the transactional order update journey.",
        setup:
          "Connect Shopify order webhooks to CleverTap via server-side integration. Ensure order events include order ID, items, shipping details, and customer identity.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Extract order details and estimate delivery",
        280,
        "You are an order fulfillment data extractor. Parse the CleverTap order event and extract complete order details regardless of payload structure.",
        "Extract from this order webhook: {webhook_payload}. Return JSON with: user_id (string), user_email (string), user_name (string), order_id (string), order_total (number), currency (string), item_count (number), top_item_name (string, most expensive item), shipping_method (string), estimated_delivery (string, date), is_first_order (boolean).",
        [
          { field: "user_id", as: "user_id", type: "string" },
          { field: "user_email", as: "user_email", type: "string" },
          { field: "user_name", as: "user_name", type: "string" },
          { field: "order_id", as: "order_id", type: "string" },
          { field: "order_total", as: "order_total", type: "number" },
          { field: "currency", as: "currency", type: "string" },
          { field: "item_count", as: "item_count", type: "number" },
          { field: "top_item_name", as: "top_item_name", type: "string" },
          { field: "shipping_method", as: "shipping_method", type: "string" },
          {
            field: "estimated_delivery",
            as: "estimated_delivery",
            type: "string",
          },
          { field: "is_first_order", as: "is_first_order", type: "boolean" },
        ],
      ),
      shopifyNode(
        "3",
        "Shopify: Fetch order fulfillment status",
        520,
        "GET",
        "orders/{order_id}/fulfillments.json",
        [],
        [
          {
            name: "fulfillments[0].status",
            customName: "fulfillment_status",
            type: "string",
          },
          {
            name: "fulfillments[0].tracking_number",
            customName: "tracking_number",
            type: "string",
          },
          {
            name: "fulfillments[0].tracking_url",
            customName: "tracking_url",
            type: "string",
          },
          {
            name: "fulfillments[0].updated_at",
            customName: "fulfillment_updated",
            type: "string",
          },
        ],
      ),
      n("4", "condition", "Check: Is this the customer's first order?", 760, {
        conditionDetails: {
          conditions: [
            { field: "{is_first_order}", operator: "equals", value: "true" },
          ],
        },
      }),
      clevertapNode(
        "5",
        "CleverTap: Send order confirmation push with tracking",
        1000,
        "POST",
        "send/push.json",
        [
          { key: "to", value: '{"identity":["{user_id}"]}' },
          { key: "tag_group", value: "order_updates" },
          {
            key: "content",
            value:
              '{"title":"Order #{order_id} confirmed!","body":"Your order with {item_count} item(s) totaling {currency}{order_total} is being prepared. {top_item_name} and more — estimated delivery: {estimated_delivery}.","actions":[{"label":"Track Order","dl":"app://orders/{order_id}"}]}',
          },
        ],
        [
          { name: "status", customName: "order_push_status", type: "string" },
          { name: "id", customName: "order_push_id", type: "string" },
        ],
      ),
      n("6", "delayAction", "Wait 7 days for delivery window", 1240, {
        delayDetails: { duration: 7, unit: "days" },
      }),
      n("7", "condition", "Check: Has the order been delivered?", 1480, {
        conditionDetails: {
          conditions: [
            {
              field: "{fulfillment_status}",
              operator: "equals",
              value: "success",
            },
          ],
        },
      }),
      emailNode(
        "8",
        "Email: Delivery confirmation with review request",
        1720,
        "{user_email}",
        "Your order #{order_id} has arrived — how was your experience?",
        "Hi {user_name},\n\nGreat news! Your order #{order_id} with {item_count} item(s) should have arrived by now.\n\nOrder summary:\n- {top_item_name} and {item_count} total items\n- Order total: {currency}{order_total}\n- Shipping: {shipping_method}\n- Tracking: {tracking_number}\n\nWe'd love to hear about your experience! Take 30 seconds to rate your purchase and help other shoppers.\n\nAs a thank you for your review, you'll receive exclusive early access to our next sale.\n\nHappy with your purchase? Share it with friends and earn rewards!",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Transactional order update journey via CleverTap and Shopify. Extracts order details via AI, fetches fulfillment status from Shopify, checks if first-time buyer, sends an order confirmation push with tracking deep link, waits 7 days for delivery, then sends a delivery confirmation email with review request and referral prompt.",
  },

  // ─── 200. Wishlist Reminder | Pattern D ───
  "Wishlist Reminder": {
    nodes: [
      trigger(
        "CleverTap: Wishlist item price drop or restock event",
        "webhook_app",
        {
          source: "clevertap",
          event: "wishlist_item_alert",
          howItStarts:
            "Fires when CleverTap detects a price drop, restock, or low inventory event for a product on a user's wishlist.",
          setup:
            "Connect Shopify product update webhooks to CleverTap and match against user wishlists. Configure alerts for price_decreased, back_in_stock, and low_inventory events on wishlisted items.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "OpenAI: Extract wishlist alert details and urgency level",
        280,
        "You are a wishlist notification specialist. Parse the CleverTap wishlist alert webhook and extract product and alert details regardless of payload format.",
        "Extract from this wishlist alert webhook: {webhook_payload}. Return JSON with: user_id (string), user_email (string), user_name (string), product_name (string), product_id (string), alert_type (string: price_drop, back_in_stock, or low_inventory), original_price (number), current_price (number), currency (string), discount_percent (number, 0 if not price drop), stock_remaining (number, 0 if unknown), urgency_level (string: low, medium, or high).",
        [
          { field: "user_id", as: "user_id", type: "string" },
          { field: "user_email", as: "user_email", type: "string" },
          { field: "user_name", as: "user_name", type: "string" },
          { field: "product_name", as: "product_name", type: "string" },
          { field: "product_id", as: "product_id", type: "string" },
          { field: "alert_type", as: "alert_type", type: "string" },
          { field: "original_price", as: "original_price", type: "number" },
          { field: "current_price", as: "current_price", type: "number" },
          { field: "currency", as: "currency", type: "string" },
          { field: "discount_percent", as: "discount_percent", type: "number" },
          { field: "stock_remaining", as: "stock_remaining", type: "number" },
          { field: "urgency_level", as: "urgency_level", type: "string" },
        ],
      ),
      shopifyNode(
        "3",
        "Shopify: Verify current product availability and pricing",
        520,
        "GET",
        "products/{product_id}.json",
        [],
        [
          {
            name: "product.title",
            customName: "verified_product_name",
            type: "string",
          },
          {
            name: "product.variants[0].price",
            customName: "verified_price",
            type: "string",
          },
          {
            name: "product.variants[0].inventory_quantity",
            customName: "verified_stock",
            type: "number",
          },
          {
            name: "product.status",
            customName: "product_status",
            type: "string",
          },
        ],
      ),
      n(
        "4",
        "condition",
        "Check: Is this a high-urgency alert (large discount or low stock)?",
        760,
        {
          conditionDetails: {
            conditions: [
              { field: "{urgency_level}", operator: "equals", value: "high" },
            ],
          },
        },
      ),
      clevertapNode(
        "5",
        "CleverTap: Send wishlist alert push notification",
        1000,
        "POST",
        "send/push.json",
        [
          { key: "to", value: '{"identity":["{user_id}"]}' },
          { key: "tag_group", value: "wishlist_alerts" },
          {
            key: "content",
            value:
              '{"title":"Your wishlisted item alert!","body":"{product_name} — {alert_type}: now {currency}{current_price} (was {currency}{original_price}). Only {stock_remaining} left!","actions":[{"label":"Buy Now","dl":"app://product/{product_id}"}]}',
          },
        ],
        [
          {
            name: "status",
            customName: "wishlist_push_status",
            type: "string",
          },
          { name: "id", customName: "wishlist_push_id", type: "string" },
        ],
      ),
      n("6", "delayAction", "Wait 24 hours for purchase action", 1240, {
        delayDetails: { duration: 24, unit: "hours" },
      }),
      n(
        "7",
        "condition",
        "Check: Is the item still available and not purchased?",
        1480,
        {
          conditionDetails: {
            conditions: [
              {
                field: "{verified_stock}",
                operator: "greater_than",
                value: "0",
              },
            ],
          },
        },
      ),
      emailNode(
        "8",
        "Email: Wishlist item follow-up with similar recommendations",
        1720,
        "{user_email}",
        "Your wishlisted {product_name} is still available — but not for long",
        "Hi {user_name},\n\nGood news — {product_name} from your wishlist is still available!\n\nAlert: {alert_type}\nCurrent price: {currency}{current_price} (was {currency}{original_price})\nStock remaining: {verified_stock} units\n\nThis is one of our most popular items and stock is limited. Don't miss out on this deal.\n\nStill deciding? Here are similar items in the same category that other shoppers loved.\n\nOpen the app to view your full wishlist and grab your favorites before they're gone.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Wishlist item alert journey via CleverTap and Shopify. Extracts wishlist alert details (price drop, restock, low inventory) via AI, verifies current product availability in Shopify, assesses urgency level, sends an immediate push notification with product deep link, waits 24 hours, then sends an email follow-up with availability reminder and similar product recommendations for non-purchasers.",
  },
};
